import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import ElementDetails from 'models/api/element-details-api-model';
import { FormattedMessage } from 'react-intl';
import { getElementResultByNumberFormat } from 'utils/formula-utils';
import { isNullOrUndefined } from 'utils/object-utils';

const AMOUNT_ATTRIBUTES_DETAILS_BLOCK = 'amount-attribute-details';
const AmountAttributesDetails = ({
  elementDetails,
  className,
  flagMap,
  selectedStatement,
  isElementPanelHeader = false, // to handle the case when this component is used in the element panel header
}) => {
  const [allItems, setAllItems] = useState({});
  const [resizeTrigger, setResizeTrigger] = useState(0);
  const [isTruncated, setIsTruncated] = useState({});

  const containerElementId = isElementPanelHeader ? 'element-panel-header-amount-attribute' : `amount_attribute_${elementDetails.id}`;

  // Ref to always get latest updateAllItems
  const updateAllItems = React.useCallback(() => {
    const containers = document.querySelectorAll(
      `.${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}`,
    );
    const allItemsMap = {};
    const truncatedMap = {};

    containers.forEach((container) => {
      const containerId = isElementPanelHeader ? 'element-panel-header-amount-attribute' : container.id;

      if (!containerId) return;

      const items = Array.from(
        container.querySelectorAll(`.${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item`),
      );
      allItemsMap[containerId] = items.map((item) => item.textContent);

      // Detect if container is truncated (ellipsis is visible)
      const isItemTruncated = container.scrollWidth > container.clientWidth;

      truncatedMap[containerId] = isItemTruncated;
    });
    setAllItems(allItemsMap);
    setIsTruncated(truncatedMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isElementPanelHeader, elementDetails]);

  useEffect(() => {
    updateAllItems();
  }, [resizeTrigger, elementDetails, isElementPanelHeader, updateAllItems]);

  useEffect(() => {
    const handleResize = () => {
      setResizeTrigger((prev) => prev + 1);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (elementDetails.isLoaded === false) {
    return null;
  }
  const numberFormatId =
    selectedStatement &&
    selectedStatement.statement &&
    selectedStatement.statement.numberFormatId;
  const periodValue = elementDetails.period && elementDetails.period.split(' ');

  // method used to removed the full-width numbers and convert them to normal numbers without any width
  const extractValidNumber = (periodType) => {
    const regex = /０?[１-９]|１０|１１|１２/;
    const match = periodType.match(regex);
    if (match) {
      // returns normal text when the regex matches
      const normalized = periodType.replace(/[０-９]/g, (char) =>
        String.fromCharCode(char.charCodeAt(0) - 0xfee0),
      );
      return normalized;
    }
    return periodType; // returns the original text when the regex doesn't match
  };

  return (
    <div
      id={containerElementId}
      className={classnames(AMOUNT_ATTRIBUTES_DETAILS_BLOCK, className)}
      title={
        isTruncated[containerElementId]
          ? allItems[containerElementId]?.join(' | ')
          : undefined
      }
      onMouseEnter={updateAllItems}
    >
      <span
        className={classnames(
          `${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item`,
          flagMap && !flagMap.scaling
            ? `${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item-flagged`
            : null,
        )}
      >
        <FormattedMessage id={elementDetails.scaling.title} />
      </span>
      <span className={classnames(`${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item`)}>
        <FormattedMessage
          id={
            isNullOrUndefined(elementDetails.amount)
              ? '0' // default actual scaled value
              : +elementDetails.amount
              ? getElementResultByNumberFormat(
                  elementDetails.amountString,
                  numberFormatId,
                  elementDetails?.scaling?.id,
                )
              : elementDetails.amount.toString()
          }
        />
      </span>
      <span
        className={classnames(
          `${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item`,
          flagMap && !flagMap.units
            ? `${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item-flagged`
            : null,
        )}
      >
        <FormattedMessage id={elementDetails.units.title} />
      </span>
      <span
        className={classnames(
          `${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item`,
          flagMap && !flagMap.period
            ? `${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item-flagged`
            : null,
        )}
      >
        {periodValue && Array.isArray(periodValue) && (
          <FormattedMessage
            id={`period.label.${extractValidNumber(periodValue[0])}`}
            values={{ year: periodValue[1] }}
          />
        )}
      </span>
      <span
        className={classnames(
          `${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item`,
          flagMap && !flagMap.entity
            ? `${AMOUNT_ATTRIBUTES_DETAILS_BLOCK}__item-flagged`
            : null,
        )}
      >
        {elementDetails.entity}
      </span>
    </div>
  );
};

AmountAttributesDetails.propTypes = {
  /** Selected elements details object */
  elementDetails: PropTypes.oneOfType([
    PropTypes.instanceOf(ElementDetails),
    PropTypes.object,
  ]).isRequired,
};

export default AmountAttributesDetails;
